import { useState } from "react";
import { PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { formatDisplay } from "@/lib/datetime-format";
import { ClassificationForm } from "@/features/closed-loop/ClassificationForm";
import {
  CAUSE_LABELS,
  NOT_CLASSIFIED_LABEL,
  PENDENCY_KIND_LABELS,
  PENDENCY_STATUS_HINTS,
  PENDENCY_STATUS_LABELS,
  PENDENCY_STATUS_TONES,
  proposalOriginLabel,
} from "@/features/closed-loop/labels";
import type { ClassificationEntry } from "@/types/closed-loop.types";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import { useAppliedScenario } from "./hooks/useAppliedScenario";

interface ClassificationSectionProps {
  scenario: AppliedScenario;
}

// Spec 077 (US1/US4) — POR QUE este compromisso divergiu.
//
// A apuração (075/076) responde "quanto"; esta seção responde "por quê", e a resposta é sempre um
// julgamento humano sobre uma proposta determinística.
//
// ⚠️ Compromisso não apurado NÃO ganha formulário desabilitado (FR-048): ganha a frase que diz
// quando a classificação abre. Um controle cinza e mudo faz a pessoa procurar o que ela fez de
// errado, quando não há nada errado — só não chegou a hora.

/** Uma entrada do histórico: a causa, quem a confirmou, o que a regra propôs, e a pendência dela. */
function HistoryEntry({ entry, isCurrent }: { entry: ClassificationEntry; isCurrent: boolean }) {
  return (
    <li className="space-y-1.5 rounded-md border border-border-subtle p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-text-primary">
          {CAUSE_LABELS[entry.cause]}
        </span>
        {/* A vigente é INEQUÍVOCA (FR-033) — cor + rótulo, nunca só a posição na lista. */}
        {isCurrent ? (
          <Pill tone="brand" withDot>
            vigente
          </Pill>
        ) : (
          <Pill tone="neutral" withDot>
            superada
          </Pill>
        )}
      </div>
      <p className="text-[12px] text-text-secondary">
        {entry.confirmedByName ?? "Autor não identificado"} · {formatDisplay(entry.confirmedAt)}
      </p>
      {/* US1 cenário 10 — o que o sistema HAVIA proposto naquela ocasião, não o que proporia hoje. */}
      <p className="text-[12px] text-text-secondary">
        {entry.proposedCause
          ? `A regra havia proposto: ${CAUSE_LABELS[entry.proposedCause]} (${proposalOriginLabel(entry.proposalOrigin)}).`
          : "Na ocasião o sistema não teve como propor uma causa."}
      </p>
      {entry.note ? <p className="text-[12px] text-text-primary">“{entry.note}”</p> : null}
      {entry.target ? (
        <p className="text-[12px] text-text-secondary">
          Alvo: {entry.target.machineName} · {entry.target.parameterName}
        </p>
      ) : null}
      {/* ⚠️ FR-027 — a pendência de CADA entrada, inclusive as superadas: o trabalho de calibração
          foi feito, e escondê-lo porque o diagnóstico mudou apagaria o registro dele. */}
      {entry.pendency ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-1.5">
          <span className="text-[12px] text-text-secondary">
            {PENDENCY_KIND_LABELS[entry.pendency.kind]}
          </span>
          <Pill tone={PENDENCY_STATUS_TONES[entry.pendency.status]} withDot>
            {PENDENCY_STATUS_LABELS[entry.pendency.status]}
          </Pill>
          <span className="text-[11px] text-text-tertiary">
            {PENDENCY_STATUS_HINTS[entry.pendency.status]}
          </span>
          {entry.pendency.resolutionNote ? (
            <span className="w-full text-[12px] text-text-primary">
              “{entry.pendency.resolutionNote}”
            </span>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function ClassificationSection({ scenario }: ClassificationSectionProps) {
  const detail = useAppliedScenario(scenario.id);
  // ⚠️ US4 (FR-028…FR-032) precisa de UMA porta. A proposta some depois da confirmação de propósito
  // — propor de novo convidaria a reclassificar por sugestão do sistema —, mas sumir com o
  // FORMULÁRIO junto deixava a correção de um diagnóstico errado sem caminho nenhum na aplicação:
  // a matriz de supersessão, o desfecho `SUPERSEDED` e o que a doc do usuário promete só eram
  // alcançáveis por um POST à mão. O botão é explícito e fica fechado por padrão: reclassificar é
  // ato deliberado, não o estado inicial da tela.
  const [reclassifying, setReclassifying] = useState(false);

  // FR-048 — antes da apuração não há o que classificar, e a tela diz isso com todas as letras.
  if (scenario.state !== "SETTLED") {
    return (
      <div className="rounded-md border border-border-subtle p-3">
        <p className="text-[13px] font-medium text-text-primary">Causa da divergência</p>
        <p className="mt-1 text-[12px] text-text-secondary">
          A classificação abre depois da apuração: só há por que divergiu quando previsto e
          realizado já foram comparados.
        </p>
      </div>
    );
  }

  // Falha de leitura NÃO pode virar "ainda não classificado" — seria trabalho inventado para
  // alguém, sobre um compromisso que talvez já tenha causa registrada.
  if (detail.isError) {
    return (
      <Notify.QueryError
        error={detail.error}
        onRetry={() => void detail.refetch()}
        title="Não foi possível carregar a causa da divergência"
      />
    );
  }
  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">Carregando a classificação…</p>;
  }

  const classification = detail.data.classification;
  if (!classification) {
    return (
      <div className="rounded-md border border-border-subtle p-3">
        <p className="text-[13px] font-medium text-text-primary">Causa da divergência</p>
        <p className="mt-1 text-[12px] text-text-secondary">
          Este compromisso ainda não tem apuração carregada — reabra a apuração para classificar.
        </p>
      </div>
    );
  }

  const history = classification.history;

  return (
    <section className="space-y-3 rounded-md border border-border-subtle p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-medium text-text-primary">Causa da divergência</h3>
        {/* ⚠️ FR-047 — "aguardando classificação" é TRABALHO DE ALGUÉM, nunca uma causa. */}
        {classification.current === null ? (
          <Pill tone="warning" withDot>
            {NOT_CLASSIFIED_LABEL}
          </Pill>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() => setReclassifying((open) => !open)}
          >
            <PencilLine className="h-4 w-4" aria-hidden />
            {reclassifying ? "Cancelar" : "Reclassificar"}
          </Button>
        )}
      </div>

      {/* FR-028 — reclassificar ACRESCENTA: o registro anterior continua no histórico logo abaixo,
          marcado como superado, e a justificativa passa a ser obrigatória (FR-029). */}
      {classification.current === null || reclassifying ? (
        <ClassificationForm
          appliedScenarioId={scenario.id}
          lineId={scenario.lineId}
          classification={classification}
          onDone={() => setReclassifying(false)}
        />
      ) : null}

      {history.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[12px] font-medium text-text-secondary">
            {history.length > 1 ? "Histórico de classificações" : "Classificação registrada"}
          </p>
          <ul className="space-y-2">
            {history.map((entry, i) => (
              <HistoryEntry key={entry.id} entry={entry} isCurrent={i === 0} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
